import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EvidenceVault from './EvidenceVault';
import { QrCode, MoreVertical, Eye, Zap, X, ChevronLeft, Lightbulb, Bot, DownloadCloud, FileArchive } from 'lucide-react';

import ReferralManagement from './ReferralManagement';
import PaymentModal from './PaymentModal';

const CaseList: React.FC = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQr, setSelectedQr] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<any | null>(null);
  const [thinkTankOutputs, setThinkTankOutputs] = useState<any[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<any | null>(null);
  const [prediction, setPrediction] = useState<any | null>(null);
  const [predicting, setPredicting] = useState(false);
  const [retrievingComposite, setRetrievingComposite] = useState(false);

  const fetchCases = async () => {
    try {
      const response = await api.get('/cases');
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadComposite = async (caseId: string) => {
    setRetrievingComposite(true);
    try {
      const response = await api.get(`/cases/${caseId}/composite`);
      const { title, compositeCopy } = response.data;
      
      if (compositeCopy.length === 0) {
        alert('No documents found for this case.');
        return;
      }

      // In a real zip scenario, we'd trigger a blob download.
      // Here we show a summary and provide links as a "composite" retrieval.
      const summary = compositeCopy.map((c: any) => `- ${c.title} (${c.type})`).join('\n');
      if (window.confirm(`Retrieving Composite Copy for "${title}"\n\nFound ${compositeCopy.length} documents:\n${summary}\n\nOpen documents in new tabs?`)) {
        compositeCopy.forEach((c: any) => window.open(c.url, '_blank'));
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to retrieve composite copy');
    } finally {
      setRetrievingComposite(false);
    }
  };

  const fetchThinkTankOutputs = async (caseId: string) => {
    try {
      const response = await api.get(`/think-tank/case/${caseId}`);
      setThinkTankOutputs(response.data);
    } catch (error) {
      console.error('Error fetching think tank outputs:', error);
    }
  };

  const handlePredictOutcome = async (caseId: string) => {
    setPredicting(true);
    try {
      const response = await api.get(`/ai/predict/${caseId}`);
      setPrediction(response.data);
    } catch (error) {
      console.error('Error predicting outcome:', error);
      alert('Failed to get prediction from AI service');
    } finally {
      setPredicting(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const handleActivateThinkTank = async (id: string) => {
    if (window.confirm('Activating Think Tank costs 10,000 PKR and provides expert + AI collaboration. Proceed?')) {
      try {
        const response = await api.post(`/cases/${id}/activate-think-tank`);
        // The backend creates a payment request. We'll show the modal for it.
        setPendingPayment({
          id: response.data.caseItem.id, // Using case ID as ref for now
          caseId: response.data.caseItem.id,
          amount: 10000,
          type: 'think_tank_fee'
        });
        setShowPaymentModal(true);
      } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to activate Think Tank');
      }
    }
  };

  const openCaseDetails = (caseItem: any) => {
    setSelectedCase(caseItem);
    setPrediction(null);
    if (caseItem.thinkTankActivated) {
      fetchThinkTankOutputs(caseItem.id);
    }
  };

  if (loading) return <div>Loading cases...</div>;

  if (selectedCase) {
    return (
      <div style={{ padding: '1rem' }}>
        <button 
          onClick={() => setSelectedCase(null)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1a365d', fontWeight: 'bold' }}
        >
          <ChevronLeft size={20} /> Back to Case List
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '2rem' }}>
          <div>
            <header style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1a365d' }}>{selectedCase.title}</h2>
                    <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', borderRadius: '9999px', backgroundColor: getStatusColor(selectedCase.status).bg, color: getStatusColor(selectedCase.status).text, fontWeight: 'bold', textTransform: 'uppercase' }}>
                      {selectedCase.status}
                    </span>
                  </div>
                  <p style={{ color: '#4a5568', fontSize: '1.1rem' }}>{selectedCase.caseId} • {selectedCase.practiceArea}</p>
                </div>
                
                {(['administrator', 'practitioner', 'law_firm', 'think_tank_member'].includes(user?.role || '')) && (
                  <button 
                    onClick={() => handleDownloadComposite(selectedCase.id)}
                    disabled={retrievingComposite}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', backgroundColor: '#2d3748', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {retrievingComposite ? <DownloadCloud size={18} className="animate-pulse" /> : <FileArchive size={18} />}
                    Retrieve Composite Copy
                  </button>
                )}
              </div>
            </header>

            <section style={{ marginBottom: '2.5rem' }}>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>Description</h4>
              <p style={{ color: '#2d3748', lineHeight: '1.6' }}>{selectedCase.description}</p>
            </section>

            <EvidenceVault caseId={selectedCase.id} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {/* AI Prediction Section */}
            <div style={{ padding: '1.5rem', backgroundColor: '#ebf8ff', borderRadius: '8px', border: '1px solid #bee3f8' }}>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2b6cb0' }}>
                <Bot size={20} /> AI Outcome Prediction
              </h4>
              {prediction ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{ flex: 1, height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${prediction.successProbability * 100}%`, height: '100%', backgroundColor: '#3182ce' }} />
                    </div>
                    <span style={{ fontWeight: 'bold', color: '#2b6cb0' }}>{Math.round(prediction.successProbability * 100)}% Success</span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#2d3748', lineHeight: '1.5' }}>{prediction.reasoning}</p>
                  <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.5rem' }}>Confidence Score: {prediction.confidenceScore}</p>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '0.875rem', color: '#2c5282', marginBottom: '1rem' }}>Get an AI-powered prediction based on similar past cases.</p>
                  <button 
                    onClick={() => handlePredictOutcome(selectedCase.id)}
                    disabled={predicting}
                    style={{ width: '100%', padding: '0.6rem', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {predicting ? 'Analyzing...' : 'Predict Outcome'}
                  </button>
                </div>
              )}
            </div>

            {/* Think Tank Section */}
            <div style={{ padding: '1.5rem', backgroundColor: '#fffaf0', borderRadius: '8px', border: '1px solid #feebc8' }}>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#9c4221' }}>
                <Lightbulb size={20} /> Think Tank Insights
              </h4>
              
              {!selectedCase.thinkTankActivated ? (
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: '#7b341e', marginBottom: '1rem' }}>Activate our Think Tank for expert research and strategy.</p>
                  <button 
                    onClick={() => handleActivateThinkTank(selectedCase.id)}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#d69e2e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    Activate Now
                  </button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {thinkTankOutputs.map((output) => (
                    <div key={output.id} style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #feebc8' }}>
                      <p style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.25rem' }}>{output.title}</p>
                      <p style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.5rem' }}>Type: {output.type.replace('_', ' ')}</p>
                      <p style={{ fontSize: '0.875rem', color: '#4a5568' }}>{output.content.substring(0, 100)}...</p>
                      <button style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#d69e2e', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>Read Full Report</button>
                    </div>
                  ))}
                  {thinkTankOutputs.length === 0 && (
                    <p style={{ fontSize: '0.875rem', color: '#7b341e', textAlign: 'center' }}>Researchers are currently working on your case.</p>
                  )}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div style={{ padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Case Info</h4>
              <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#718096' }}>Court:</span>
                  <span style={{ fontWeight: 'bold' }}>{selectedCase.courtType}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#718096' }}>Estimated Fee:</span>
                  <span style={{ fontWeight: 'bold' }}>PKR {selectedCase.estimatedFee}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#718096' }}>Paid Amount:</span>
                  <span style={{ fontWeight: 'bold' }}>PKR {selectedCase.paidAmount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#1a365d' }}>My Cases</h3>
      
      <div style={{ display: 'grid', gap: '1rem' }}>
        {cases.map((caseItem) => (
          <div 
            key={caseItem.id} 
            onClick={() => openCaseDetails(caseItem)}
            style={{ 
              padding: '1.5rem', 
              backgroundColor: 'white', 
              borderRadius: '8px', 
              border: '1px solid #e2e8f0', 
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 'bold', color: '#1a365d' }}>{caseItem.caseId}</span>
                <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.7rem', borderRadius: '9999px', backgroundColor: getStatusColor(caseItem.status).bg, color: getStatusColor(caseItem.status).text, fontWeight: 'bold', textTransform: 'uppercase' }}>
                  {caseItem.status}
                </span>
              </div>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>{caseItem.title}</h4>
              <p style={{ color: '#718096', fontSize: '0.875rem' }}>{caseItem.practiceArea} • {caseItem.courtType}</p>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }} onClick={(e) => e.stopPropagation()}>
              {!caseItem.thinkTankActivated && (
                <button 
                  onClick={() => handleActivateThinkTank(caseItem.id)} 
                  title="Activate Think Tank" 
                  style={{ ...iconButtonStyle, color: '#d69e2e', borderColor: '#d69e2e' }}
                >
                  <Zap size={20} />
                </button>
              )}
              {caseItem.thinkTankActivated && (
                <span title="Think Tank Active" style={{ color: '#d69e2e', display: 'flex', alignItems: 'center', padding: '0.5rem' }}>
                  <Zap size={20} fill="#d69e2e" />
                </span>
              )}
              <button 
                title="View QR Code" 
                style={iconButtonStyle}
                onClick={(e) => { e.stopPropagation(); setSelectedQr(caseItem.qrCode); }}
                disabled={!caseItem.qrCode}
              >
                <QrCode size={20} />
              </button>
              <button onClick={() => openCaseDetails(caseItem)} title="View Details" style={iconButtonStyle}><Eye size={20} /></button>
              <button title="More Actions" style={iconButtonStyle}><MoreVertical size={20} /></button>
            </div>
          </div>
        ))}
        {cases.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '2px dashed #e2e8f0' }}>
            <p style={{ color: '#718096' }}>No cases found. Register a case to get started.</p>
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedQr && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', maxWidth: '400px', width: '90%', textAlign: 'center', position: 'relative' }}>
            <button 
              onClick={() => setSelectedQr(null)}
              style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}
            >
              <X size={24} />
            </button>
            <h3 style={{ marginBottom: '1.5rem', color: '#1a365d' }}>Case QR Code</h3>
            <img src={selectedQr} alt="Case QR Code" style={{ width: '100%', maxWidth: '250px', margin: '0 auto 1.5rem' }} />
            <p style={{ fontSize: '0.875rem', color: '#4a5568' }}>Scan this code to track your case status instantly on our mobile app or website.</p>
            <button 
              onClick={() => setSelectedQr(null)}
              style={{ marginTop: '1.5rem', width: '100%', padding: '0.75rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && pendingPayment && (
        <PaymentModal 
          paymentData={pendingPayment}
          onClose={() => { setShowPaymentModal(false); setPendingPayment(null); }}
          onSuccess={() => { fetchCases(); alert('Payment successfully processed through gateway!'); }}
        />
      )}
    </div>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return { bg: '#fffaf0', text: '#9c4221' };
    case 'registered': return { bg: '#ebf8ff', text: '#2b6cb0' };
    case 'assigned': return { bg: '#f0fff4', text: '#276749' };
    case 'in_progress': return { bg: '#faf5ff', text: '#6b46c1' };
    case 'decided': return { bg: '#f0fff4', text: '#22543d' };
    default: return { bg: '#edf2f7', text: '#4a5568' };
  }
};

const iconButtonStyle = { padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', backgroundColor: 'transparent', cursor: 'pointer', color: '#4a5568' };

export default CaseList;
